import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import Swal from 'sweetalert2';

import { AuthService } from '@app/core/auth';

//

@Component({
  selector: 'cnc-auth-forgot',
  templateUrl: './auth-forgot.component.html',
  styleUrls: ['./auth-forgot.component.scss'],
})
export class AuthForgotComponent implements OnInit, OnDestroy {
  @HostBinding('class') _hostClass = 'lum-login-body';

  // Public params
  forgotPasswordForm :UntypedFormGroup;
  loading = false;
  errors :any = [];

  private unsubscribe :Subject<any>; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  /**
   * Component constructor
   *
   * @param authService
   * @param authNoticeService
   * @param translate
   * @param router
   * @param fb
   * @param cdr
   */
  constructor(
    private authService :AuthService,
    private router :Router,
    private fb :UntypedFormBuilder,
    private cdr :ChangeDetectorRef,
    private translate :TranslateService,
  ) {
    this.unsubscribe = new Subject();
  }

  /**
   * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
   */

  /**
   * On init
   */
  ngOnInit() {
    this.initRegistrationForm();
  }

  /**
   * On destroy
   */
  ngOnDestroy() :void {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
    this.loading = false;
  }

  handleSubmit(e :SubmitEvent) {
    e.preventDefault();

    this.#submit();
  }

  /**
   * Form initalization
   * Default params, validators
   */
  initRegistrationForm() {
    this.forgotPasswordForm = this.fb.group({
      email: [
        '',
        Validators.compose([
          Validators.required,
          Validators.email,
          Validators.minLength(3),
          Validators.maxLength(320), // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
        ]),
      ],
    });
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to valitors name
   */
  isControlHasError(controlName :string, validationType :string) :boolean {
    const control = this.forgotPasswordForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result =
      control.hasError(validationType) && (control.dirty || control.touched);

    return result;
  }

  #submit() {
    const controls = this.forgotPasswordForm.controls;
    /** check form */
    if (this.forgotPasswordForm.invalid) {
      Object.keys(controls).forEach((controlName) =>
        controls[controlName].markAsTouched(),
      );

      return;
    }
    const email = controls['email'].value;
    this.loading = true;

    this.authService.forgotPassword(email).subscribe(
      () => {
        this.loading = false;
        this.cdr.markForCheck();
        Swal.fire({
          title: this.translate.instant('MESSAGES.PASSWORD_RECOVERY.TITLE'),
          text: this.translate.instant('MESSAGES.PASSWORD_RECOVERY.TEXT'),
          type: 'success',
          confirmButtonText: this.translate.instant('BUTTONS.OKAY'),
        }).then(() => {
          this.router.navigateByUrl('/auth/login');
        });
      },
      (error) => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    );
  }
}
